import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { graphql } from 'gatsby';

import { useDualToneTextData } from './hooks';
import { VARIANT } from './constants';

import { RichText } from 'components/Prismic';
import LineClamp from 'components/LineClamp';
import { Normalize } from 'utils/types';

export type TDualToneTextSection =
  Normalize<Queries.SolutionDesktopDualToneTextData_PrismicSolutionDataDesktopSolutionDualToneText_Fragment>;

enum DUAL_TONE_TEXT_VARIABLES {
  titleColor = '--title-color',
  titleShadow = '--title-shadow',
  titleLineHeight = '--title-line-height',
  textColor = '--text-color',
  topColor = '--top-color',
  bottomColor = '--bottom-color',
}

const generateVariantColorMap = ({ colors }: Theme) => ({
  [VARIANT.LIGHT]: {
    [DUAL_TONE_TEXT_VARIABLES.titleColor]: colors.black,
    [DUAL_TONE_TEXT_VARIABLES.titleShadow]: 'initial',
    [DUAL_TONE_TEXT_VARIABLES.titleLineHeight]: 1.04,
    [DUAL_TONE_TEXT_VARIABLES.textColor]: colors.white,
    [DUAL_TONE_TEXT_VARIABLES.topColor]: colors.offWhite,
    [DUAL_TONE_TEXT_VARIABLES.bottomColor]: '#757575',
  },
  [VARIANT.DARK]: {
    [DUAL_TONE_TEXT_VARIABLES.titleColor]: colors.black,
    [DUAL_TONE_TEXT_VARIABLES.titleShadow]: `
      0.25rem 0.125rem 0 #646464
    `,
    [DUAL_TONE_TEXT_VARIABLES.titleLineHeight]: 0.905,
    [DUAL_TONE_TEXT_VARIABLES.textColor]: colors.white,
    [DUAL_TONE_TEXT_VARIABLES.topColor]: '#757575',
    [DUAL_TONE_TEXT_VARIABLES.bottomColor]: '#141414',
  },
  [VARIANT.SKY]: {
    [DUAL_TONE_TEXT_VARIABLES.titleColor]: colors.white,
    [DUAL_TONE_TEXT_VARIABLES.titleShadow]: `
      0.25rem 0.125rem 0.875rem #646464
    `,
    [DUAL_TONE_TEXT_VARIABLES.titleLineHeight]: 0.905,
    [DUAL_TONE_TEXT_VARIABLES.textColor]: colors.white,
    [DUAL_TONE_TEXT_VARIABLES.topColor]: '#3A485F',
    [DUAL_TONE_TEXT_VARIABLES.bottomColor]: '#7B7B7B',
  },
});

const Container = styled('div', {
  shouldForwardProp: prop => prop !== 'variant',
})<{
  variant: VARIANT;
}>`
  ${({ theme, variant }) => {
    const variantColorMap = generateVariantColorMap(theme)[variant];

    const { mq } = theme;

    return css`
      ${DUAL_TONE_TEXT_VARIABLES.titleColor}: ${variantColorMap[
        DUAL_TONE_TEXT_VARIABLES.titleColor
      ]};
      ${DUAL_TONE_TEXT_VARIABLES.titleShadow}: ${variantColorMap[
        DUAL_TONE_TEXT_VARIABLES.titleShadow
      ]};
      ${DUAL_TONE_TEXT_VARIABLES.titleLineHeight}: ${variantColorMap[
        DUAL_TONE_TEXT_VARIABLES.titleLineHeight
      ]};
      ${DUAL_TONE_TEXT_VARIABLES.textColor}: ${variantColorMap[DUAL_TONE_TEXT_VARIABLES.textColor]};
      ${DUAL_TONE_TEXT_VARIABLES.topColor}: ${variantColorMap[DUAL_TONE_TEXT_VARIABLES.topColor]};
      ${DUAL_TONE_TEXT_VARIABLES.bottomColor}: ${variantColorMap[
        DUAL_TONE_TEXT_VARIABLES.bottomColor
      ]};

      display: grid;
      grid-template-rows: 1fr 1fr;
      height: 139.625rem;

      ${mq.md} {
        grid-template-rows: auto auto;
        height: initial;

        && {
          display: grid;
        }
      }
    `;
  }}
`;

const TitleContainer = styled.div`
  ${({ theme: { mq, spacings } }) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    background-color: var(${DUAL_TONE_TEXT_VARIABLES.topColor});
    padding: 0 12.5rem 2rem;

    ${mq.md} {
      padding: 7.5rem ${spacings.padding} 3.5rem;
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { tp, mq } }) => css`
    display: flex;
    flex-direction: column;

    & > h2 {
      text-align: left;
      text-shadow: var(${DUAL_TONE_TEXT_VARIABLES.titleShadow});
      line-height: var(${DUAL_TONE_TEXT_VARIABLES.titleLineHeight});
      letter-spacing: -0.1em;
      color: var(${DUAL_TONE_TEXT_VARIABLES.titleColor});
      font-size: 12.25rem;

      ${tp.useFont(fonts => fonts.Switzer.lightest)};
    }

    ${mq.md} {
      & > h2 {
        font-size: 4rem;

        br {
          display: none;
        }
      }
    }
  `}
`;

const DescriptionContainer = styled.div`
  ${({ theme: { mq, spacings } }) => css`
    background-color: var(${DUAL_TONE_TEXT_VARIABLES.bottomColor});
    padding: 1.5rem 12.5rem 0;

    ${mq.md} {
      padding: 3rem ${spacings.padding} 5.5rem;
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    position: relative;

    & > p {
      text-align: left;
      line-height: 1.2;
      color: var(${DUAL_TONE_TEXT_VARIABLES.textColor});
      font-size: 3.25rem;

      ${tp.useFont(fonts => fonts.Switzer.lightest)};
    }

    ${mq.md} {
      & > p {
        font-size: 2rem;

        br {
          display: none;
        }
      }
    }
  `}
`;

const DualToneTextSection: React.FC<TDualToneTextSection> = sliceData => {
  const { variant, title, description } = useDualToneTextData(sliceData);

  return (
    <Container variant={variant}>
      <TitleContainer>
        <Title render={title} />
      </TitleContainer>
      <DescriptionContainer>
        <LineClamp>
          <Description render={description} />
        </LineClamp>
      </DescriptionContainer>
    </Container>
  );
};

export const desktopDualToneTextFragment = graphql`
  fragment SolutionDesktopDualToneTextData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionDualToneText {
      slice_type

      primary {
        solution_dual_tone_text_variant
        solution_dual_tone_text_title {
          richText
        }
        solution_dual_tone_text_description {
          richText
        }
      }
    }
  }
`;

export default DualToneTextSection;
